import React, {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useMemo,
  useImperativeHandle,
} from 'react';
import { Space, Popover, Button, Tooltip, Input } from '@bedrock/components';
import { ChevronDown, Upload } from '@bedrock/icons-react';
import { useMemoizedFn } from 'ahooks';
import classNames from 'classnames/bind';
import { SUpload } from '../SUpload';
import TextAreaShow from '#components/SForm/SFormElement/TextAreaShow';
import TextAreaPopover, {
  TextAreaPopconfirmProps,
} from '#components/SForm/SFormElement/TextAreaPropover';
import styles from './index.less';

const cx = classNames.bind(styles);

type TextAreaWithUploadProps = {
  itemKey?: string | number;
  value?: {
    text: string;
    files: any[];
    itemKey: string | number;
  };
  onChange?: (value: {
    text: string;
    files: any[];
    itemKey: string | number;
  }) => void;
  view?: boolean;
  /** 布局：inline 横向（默认，textarea + 右侧附件按钮，附件列表 Popover 展开）；block 上下（正常 textarea + SUpload 直列，无 Popover） */
  displayType?: 'inline' | 'block';
  maxFileSize?: number;
  /**
   * 弹层 z-index（内部 TextAreaPopover 与附件列表 Popover 共用）。
   * 默认不传：跟随 @bedrock 动态分配器，与宿主抽屉按打开顺序自然分层；
   * 仅当宿主为固定层级容器、动态值可能被压制时显式传入（如充值/退款抽屉 Z_INDEX.drawerPopup）。
   */
  zIndex?: number;
  textAreaPopoverProps?: TextAreaPopconfirmProps;
  textAreaProps?: React.ComponentProps<typeof Input.TextArea>;
  uploadProps?: React.ComponentProps<typeof SUpload>;
  size?: 'small' | 'medium' | 'large';
};

const TextAreaWithUpload = forwardRef((props: TextAreaWithUploadProps, ref) => {
  const {
    itemKey,
    maxFileSize = 200 * 1024 * 1024,
    textAreaProps,
    uploadProps,
    zIndex,
    size = 'medium',
    displayType = 'inline',
  } = props;
  const {
    value = { text: '', files: [], itemKey },
    onChange,
    view = false,
  } = props;
  const uploadRef = useRef<any>(null);

  const [localFileList, setLocalFileList] = useState<any[]>(value?.files || []);

  const isUploading = useMemo(() => {
    return localFileList.some((n) => n.status === 'uploading');
  }, [localFileList]);

  const validateFields = useMemoizedFn(() => {
    return new Promise((resolve, reject) => {
      if (uploadRef.current) {
        uploadRef.current
          ?.validateFields?.()
          .then(() => {
            if (value.text || value.files?.length) {
              resolve(true);
            } else {
              reject('请输入结案报告或上传文件');
            }
          })
          .catch(reject);
      } else {
        resolve(true);
      }
    });
  });

  useImperativeHandle(ref, () => {
    return {
      validateFields,
    };
  });

  // block 布局：正常 textarea（编辑用 Input.TextArea，只读用 TextAreaShow 对齐 SForm textarea view）+ SUpload 上下直列，
  // 无 Popover（SUploadWrapper 非 inline 即"上传按钮 + 文件列表"直列形态）；绑 uploadRef 保留上传中校验
  if (displayType === 'block') {
    return (
      <div className="flex flex-col gap-1">
        <div>
          {view ? (
            <TextAreaShow value={value.text} linkDetector />
          ) : (
            <Input.TextArea
              value={value.text}
              autoComplete="off"
              onChange={(e) =>
                onChange?.({ ...value, itemKey, text: e.target.value } as any)
              }
              {...textAreaProps}
            />
          )}
        </div>
        <div>
          <SUpload
            ref={uploadRef}
            view={view}
            fileList={value.files as any}
            // 附件单个文件不超过 200M（maxFileSize 默认 200M，FormModel elementProps 显式传 200M）
            fileSizeLimit={maxFileSize}
            multiple
            tip={null}
            onChange={(file) => {
              onChange?.({ ...value, itemKey, files: file.dataList } as any);
              setLocalFileList(file.fileList);
              uploadProps?.onChange?.(file);
            }}
            {...uploadProps}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cx('closing-report')}>
      <div className={cx('text-area-container')}>
        <TextAreaPopover
          view={view}
          value={value.text}
          linkDetector
          onChange={(e: string) => {
            onChange?.({ ...value, itemKey, text: e?.trim?.() || '' } as any);
          }}
          zIndex={zIndex}
          {...props.textAreaPopoverProps}
          textAreaProps={textAreaProps}
        />
      </div>
      <Space className={cx('upload-container')} size={[8, 0]} align="start">
        {!view && (
          <SUpload
            className={cx('upload-button')}
            ref={uploadRef}
            trigger={
              <Tooltip
                title={`上传附件：单个文件不超过 ${(maxFileSize / 1024 / 1024).toFixed(0)}M`}
                placement="topRight"
              >
                <Button
                  type="checked-neutral"
                  className={cx('upload-trigger')}
                  icon={<Upload />}
                  loading={isUploading}
                  size={props?.textAreaPopoverProps?.defaultChildrenProps?.size}
                />
              </Tooltip>
            }
            tip={null}
            {...uploadProps}
            fileList={value.files as any}
            onChange={(file) => {
              onChange?.({ ...value, itemKey, files: file.dataList } as any);
              setLocalFileList(file.fileList);
              uploadProps?.onChange?.(file);
            }}
          />
        )}
        {!!value?.files?.length && (
          <Popover
            content={
              <div className={cx('upload-popover-content')}>
                <SUpload
                  className={cx('upload-list')}
                  fileSizeLimit={maxFileSize}
                  multiple
                  view={view}
                  tip={null}
                  {...uploadProps}
                  fileList={value.files as any}
                  onChange={(file) => {
                    onChange?.({ ...value, files: file.dataList } as any);
                    setLocalFileList(file.fileList);
                    uploadProps?.onChange?.(file);
                  }}
                />
              </div>
            }
            trigger="click"
            zIndex={zIndex}
            disabled={!value?.files?.length}
          >
            <Button
              type="link"
              className={cx('upload-trigger')}
              suffixIcon={<ChevronDown />}
              size={size}
            >
              {view
                ? `${value?.files?.length || 0}个附件`
                : `共${value?.files?.length || 0}个`}
            </Button>
          </Popover>
        )}
        {!value?.files?.length && !!view && (
          <span className={cx('file-empty-tip')}>无附件</span>
        )}
      </Space>
    </div>
  );
});

export default TextAreaWithUpload;
